/**
 * PHARMAKON — Fournisseur ERP · preview-gate context + 402 detector
 * (Module 00, Appendix E.11)
 *
 * The non-component half of the RequireModule guard: the context gated pages
 * read to render limited data, its hook, and the predicate that recognises the
 * server's "module not subscribed" envelope.
 *
 * Split out of RequireModule.jsx so that file exports only the guard component.
 * A module exporting components alongside a context/hook/helper cannot be
 * hot-swapped by React Fast Refresh (react-refresh/only-export-components),
 * and this helper is imported by ~30 pages and two stores — several of which
 * are not component modules at all.
 */
import * as React from "react";

/** Context surfaced to gated pages so they can render limited data. */
export const PreviewContext = React.createContext({
  preview: false,
  module: null,
  enterPreview: () => {},
});

/** Hook for gated pages: `{ preview, module, enterPreview }`. */
export const usePreview = () => React.useContext(PreviewContext);

/**
 * True for the 402 module-not-subscribed envelope. The api.client interceptor
 * rejects with `err.response?.data ?? err`, so the caught value is the BODY.
 *
 * The server ships TWO different 402 bodies and both must be recognised:
 *
 *  1. HARD gate — middleware/licence.js requireModule / requireModulePreview,
 *     rendered by the global error handler:
 *       { success:false, code:"MODULE_NOT_SUBSCRIBED", message,
 *         details:{ module, preview:true } }
 *
 *  2. SOFT gate — the analytics controllers' `preview(res, body)` helper, which
 *     returns HTTP 402 with a SUCCESS-shaped body carrying the real-but-limited
 *     payload:
 *       { success:true, data:{ …rows, preview:true, module } }
 *
 * Shape 2 has no top-level `code` and no top-level `preview` (the flag lives
 * one level down under `data`), and `err.response` is already gone by the time
 * the interceptor rejects — so the previous three checks all missed it and the
 * gated analytics routes rendered a dead error instead of the preview they were
 * literally handed. `data.preview` / `details.preview` close that gap.
 */
export const is402PreviewError = (err) =>
  err?.code === "MODULE_NOT_SUBSCRIBED" ||
  err?.preview === true ||
  err?.data?.preview === true ||
  err?.details?.preview === true ||
  err?.response?.status === 402 ||
  err?.response?.data?.data?.preview === true;

/**
 * The real-but-limited payload carried by a SOFT-gate 402, or `null` when the
 * rejection carries no preview data (hard gate / plain failure). Lets a page or
 * store adopt the capped rows instead of throwing them away — that payload is
 * the whole point of the "never a dead page" contract (BR-ANA-05).
 */
export const previewPayloadOf = (err) => {
  if (!err) return null;
  const body = err?.response?.data ?? err;
  const data = body?.data;
  if (data && typeof data === "object" && data.preview === true) return data;
  if (body && typeof body === "object" && body.preview === true && body.success !== false) {
    return body;
  }
  return null;
};

/** The paid module code a 402 rejection names, when it names one. */
export const previewModuleOf = (err) => {
  const body = err?.response?.data ?? err;
  return body?.details?.module ?? body?.data?.module ?? body?.module ?? null;
};
